// Pure URL-param hydration for the Compare page's stacked filters (PR-C added
// independent dex-type + region filters; PR-D #7 lets a deep-link pre-apply
// them). No React/DOM deps so it can be unit-tested with plain `tsx`.

import type { DexCategory } from "./data";

const DEX_CATEGORIES: readonly DexCategory[] = ["main", "shiny", "lucky", "perfect", "purified", "xxl", "xxs"];

export interface CompareFilterParams {
  category: DexCategory | null;
  region: string | null;
}

/**
 * Read `category` and `region` from a query string. `category` accepts the
 * four dex variants (with `pokemon` as an alias for `main`, matching the Dex
 * tab's `?filter=` vocabulary). `region` is returned verbatim when present so
 * the caller can validate it against its own region list. Unknown values yield
 * null so the caller keeps its default.
 */
export function parseCompareFilters(
  search: string,
  isValidRegion: (value: string) => boolean,
): CompareFilterParams {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search ?? "");
  } catch {
    return { category: null, region: null };
  }

  const rawCat = params.get("category");
  let category: DexCategory | null = null;
  if (rawCat) {
    const normalized = rawCat === "pokemon" ? "main" : rawCat;
    if ((DEX_CATEGORIES as readonly string[]).includes(normalized)) {
      category = normalized as DexCategory;
    }
  }

  const rawRegion = params.get("region");
  const region = rawRegion && isValidRegion(rawRegion) ? rawRegion : null;

  return { category, region };
}

/**
 * Extract the query-string portion from a wouter hash-router URL. Hash routing
 * keeps the query after the route (e.g. `#/friends/3/compare?category=shiny`),
 * but a plain `?category=...` search also works.
 */
export function queryStringFromLocation(hash: string, search: string): string {
  const fromHash = hash.split("?")[1];
  if (fromHash) return fromHash;
  return (search ?? "").replace(/^\?/, "");
}
